import React, { FunctionComponent, useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import QRCode from 'react-qr-code';
import { Copy, ExternalLink, PanelsTopLeft, Printer } from 'lucide-react';
import { cn } from '@companion-professional/webutils';
import {
  Button,
  buttonVariants,
  DialogClose,
  DialogFooter,
  ErrorAlert,
  Input,
  toast,
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@companion-professional/components';
import { printCheckupQRCode } from './printCheckupQRCode';

interface CheckupDialogDisplayAndFooterProps {
  checkupUrl: string;
  checkupId: string;
}

// CheckupDialogDisplayAndFooter is a component that displays the checkup URL and a QR code for the checkup URL.  It
// also provides the dialog footer with buttons to open the checkup details page, open the checkup in a new tab, copy
// the checkup URL to the clipboard and close the dialog.
export const CheckupDialogDisplayAndFooter: FunctionComponent<CheckupDialogDisplayAndFooterProps> = ({
  checkupUrl,
  checkupId
}) => {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const copyCheckupUrlToClipboard = useCallback(async () => {
    if (!checkupUrl || checkupUrl === '') {
      // Do nothing if there is no checkup url
      return;
    }

    try {
      await navigator.clipboard.writeText(checkupUrl);
      toast('Request URL copied to clipboard', {
        description: checkupUrl
      });
    } catch (err) {
      const msg = `Could not copy text "${checkupUrl}" to clipboard`;
      setErrorMessage(msg);
      console.error(msg, err);
    }
  }, [checkupUrl]);

  const handlePrint = useCallback(() => {
    if (!checkupUrl || checkupUrl === '') {
      return;
    }

    // Find the QR code SVG element in the DOM
    const qrCodeContainer = document.getElementById('checkup-qr-code');
    const qrCodeElement = qrCodeContainer?.querySelector('svg');
    if (!qrCodeElement) {
      setErrorMessage('Could not find QR code to print.');
      return;
    }

    // Get the SVG as a string
    const qrCodeSvg = qrCodeElement.outerHTML;

    const success = printCheckupQRCode(qrCodeSvg);
    if (!success) {
      setErrorMessage('Could not open print window. Please check your popup blocker settings.');
    }
  }, [checkupUrl]);

  return (
    <>
      <div className="flex flex-col gap-4">
        {errorMessage ? <ErrorAlert title="Error" message={errorMessage} /> : null}

        <div className="w-full">
          <Input id="link" defaultValue={checkupUrl} readOnly onClick={copyCheckupUrlToClipboard} />
        </div>

        <div className="flex justify-center" id="checkup-qr-code">
          <QRCode value={checkupUrl} size={256} />
        </div>
      </div>
      <DialogFooter className="flex flex-row justify-between space-x-2">
        <div className="flex flex-1 flex-row gap-2">
          <Tooltip>
            <TooltipTrigger
              onClick={() => {
                navigate(`/checkup/${checkupId}`);
              }}
            >
              <div className={cn(buttonVariants({ variant: 'primary', size: 'icon' }))}>
                <PanelsTopLeft size="18" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Open the checkup details page</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger
              onClick={() => {
                window.open(checkupUrl, '_blank');
              }}
            >
              <div className={cn(buttonVariants({ variant: 'primary', size: 'icon' }))}>
                <ExternalLink size="18" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Open the checkup in a new tab.</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger>
              <div
                className={cn('px-3', buttonVariants({ variant: 'primary', size: 'icon' }))}
                onClick={copyCheckupUrlToClipboard}
              >
                <Copy size="18" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Copy Checkup URL to your clipboard.</p>
            </TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger>
              <div
                className={cn('px-3', buttonVariants({ variant: 'primary', size: 'icon' }))}
                onClick={handlePrint}
              >
                <Printer size="18" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p>Print QR code.</p>
            </TooltipContent>
          </Tooltip>
        </div>
        <DialogClose asChild>
          <div>
            <Button size="small" type="button" variant="secondary" className="h-10">
              Close
            </Button>
          </div>
        </DialogClose>
      </DialogFooter>
    </>
  );
};
